import React, { useState, useEffect } from 'react';
import { Phone, Clock, CheckCircle2, Star, Shield, Zap, Car, Key, Lock, Wrench, Users, Award, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { initGA4, initHotjar, setupScrollTracking, trackPhoneClick, trackCTAClick, trackEvent } from '../lib/analytics';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    // Initialize analytics
    initGA4();
    initHotjar();
    
    // Setup scroll tracking
    const cleanup = setupScrollTracking();
    
    // Track page view
    trackEvent('page_view', {
      page_title: 'Bilnyckel Akuten - Landningssida',
      page_location: window.location.href,
    });

    return cleanup;
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend only - store in localStorage for demo
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, date: new Date().toISOString() });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
    alert('Tack för din förfrågan! Vi kontaktar dig inom kort. För akut hjälp, ring 076-005 25 99 nu!');
    setFormData({ name: '', phone: '', message: '' });
  };

  const handleCallClick = (source = 'general') => {
    trackPhoneClick(source);
    window.location.href = 'tel:0760052599';
  };

  return (
    <div className="min-h-screen bg-[#151515]">
      {/* Sticky Call Button - Mobile */}
      <div className="fixed bottom-6 right-6 z-50 md:hidden">
        <Button 
          onClick={() => handleCallClick('mobile_floating')}
          className="h-16 w-16 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-2xl shadow-red-500/50 ring-4 ring-red-500/20 animate-pulse-subtle hover:shadow-red-500/70 transition-all"
        >
          <Phone className="h-7 w-7" />
        </Button>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28">
            <div className="flex items-center py-4">
              <img 
                src="/logo.png" 
                alt="Bilnyckel Akuten AB" 
                className="h-20 md:h-24 w-auto object-contain drop-shadow-2xl"
              />
            </div>
            <Button 
              onClick={() => handleCallClick('header')}
              className="hidden md:flex bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-6 py-6 text-lg shadow-2xl shadow-red-500/40 hover:shadow-red-500/60 transition-all"
            >
              <Phone className="mr-2 h-5 w-5" />
              076-005 25 99
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 px-4 overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 via-transparent to-blue-600/10"></div>
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-red-600/20 blur-[120px] rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-blue-600/20 blur-[120px] rounded-full"></div>
        </div>
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1614200179396-2bdb77ebf81b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXJ8ZW58MHx8fHwxNzc0ODgzNjczfDA&ixlib=rb-4.1.0&q=85"
            alt="Premium Car"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#151515] via-[#151515]/95 to-[#151515]"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center space-y-8">
            <div className="inline-flex items-center space-x-3 bg-red-600/20 border-2 border-red-500 px-6 py-3 rounded-full animate-pulse">
              <div className="h-3 w-3 bg-red-500 rounded-full animate-ping absolute"></div>
              <div className="h-3 w-3 bg-red-500 rounded-full"></div>
              <span className="text-red-400 font-bold text-lg">JOUR DYGNET RUNT • 24/7</span>
            </div>
            
            <h2 className="text-6xl md:text-8xl font-black text-white leading-tight tracking-tight">
              Tappat <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">bilnyckeln</span>?
              <br />Vi löser det <span className="text-red-500">direkt</span>
            </h2>
            
            <p className="text-2xl md:text-3xl text-gray-200 font-semibold max-w-3xl mx-auto">
              Snabb hjälp på plats – dygnet runt
            </p>

            <div className="pt-8">
              <div className="bg-gradient-to-r from-red-600/20 to-blue-600/20 border-2 border-red-500/50 rounded-3xl p-4 md:p-8 max-w-2xl mx-auto backdrop-blur-sm">
                <p className="text-white text-lg md:text-xl font-semibold mb-4">Ring för omedelbar hjälp:</p>
                <a 
                  href="tel:0760052599"
                  onClick={() => trackPhoneClick('hero_primary')}
                  className="group block"
                >
                  <div className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-2xl p-4 md:p-6 transform hover:scale-105 transition-all duration-300 shadow-2xl shadow-red-500/60 hover:shadow-red-500/80 ring-2 ring-red-500/20 hover:ring-red-500/40">
                    <div className="flex items-center justify-center space-x-2 md:space-x-4">
                      <Phone className="h-8 w-8 md:h-10 md:w-10 text-white animate-pulse drop-shadow-lg" />
                      <span className="text-3xl md:text-5xl lg:text-6xl font-black text-white tracking-tight drop-shadow-lg">076-005 25 99</span>
                    </div>
                  </div>
                </a>
                <p className="text-gray-300 text-xs md:text-sm mt-4">Vi svarar direkt och är på plats snabbt</p>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-6 pt-8">
              <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-white font-bold text-lg">5.0</span>
              </div>
              <div className="flex items-center space-x-2 text-white bg-red-600/20 px-4 py-2 rounded-full border border-red-500/30">
                <Zap className="h-5 w-5 text-red-400" />
                <span className="font-semibold">Snabb utryckning</span>
              </div>
              <div className="flex items-center space-x-2 text-white bg-blue-600/20 px-4 py-2 rounded-full border border-blue-500/30">
                <Shield className="h-5 w-5 text-blue-400" />
                <span className="font-semibold">Erfaren tekniker</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="py-12 px-6 md:py-16 md:px-4 bg-gradient-to-r from-red-600 via-red-700 to-red-600 shadow-2xl shadow-red-900/50 relative overflow-hidden">
        {/* Animated glow */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-red-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-red-400 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-black text-white mb-2">30 min</div>
              <p className="text-red-100 text-base md:text-lg font-semibold">Genomsnittlig svarstid</p>
              <p className="text-red-200 text-xs md:text-sm mt-1">Vi är på plats snabbt</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-white mb-2">24/7</div>
              <p className="text-red-100 text-base md:text-lg font-semibold">Jour dygnet runt</p>
              <p className="text-red-200 text-xs md:text-sm mt-1">Alltid tillgängliga</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-white mb-2">100%</div>
              <p className="text-red-100 text-base md:text-lg font-semibold">Nöjda kunder</p>
              <p className="text-red-200 text-xs md:text-sm mt-1">5/5 i betyg på Google</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#151515] via-[#1a1a1a] to-[#151515] relative">
        {/* Subtle gradient accents */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-black text-white mb-4">
              Vi Hjälper Dig Med
            </h3>
            <p className="text-xl text-gray-400">Professionell service på plats – snabbt och enkelt</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              {
                icon: Key,
                title: 'Ny Bilnyckel',
                description: 'Tillverkning och programmering av nya bilnycklar',
                urgent: true
              },
              {
                icon: Lock,
                title: 'Upplåsning',
                description: 'Snabb och skonsam upplåsning av låsta fordon',
                urgent: true
              },
              {
                icon: Wrench,
                title: 'Programmering',
                description: 'Modern programmering av transpondrar',
                urgent: false
              },
              {
                icon: Car,
                title: 'Reparation',
                description: 'Reparation av defekta nycklar och fjärrkontroller',
                urgent: false
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-white/10 hover:border-red-500 rounded-2xl p-8 text-center transform hover:scale-105 transition-all duration-300"
              >
                <div className={`inline-block p-6 rounded-full ${service.urgent ? 'bg-gradient-to-br from-red-600 to-red-700' : 'bg-gradient-to-br from-blue-600 to-blue-700'} mb-4`}>
                  <service.icon className="h-10 w-10 text-white" />
                </div>
                {service.urgent && (
                  <div className="inline-block bg-red-600/20 border border-red-500/50 text-red-400 text-xs font-bold px-3 py-1 rounded-full mb-3">
                    AKUT SERVICE
                  </div>
                )}
                <h4 className="text-2xl font-bold text-white mb-3">{service.title}</h4>
                <p className="text-gray-400 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof - Real Google & TikTok Reviews */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#111111] to-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-10 w-10 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <h3 className="text-4xl md:text-5xl font-black text-white mb-2">
              5.0 Stjärnor
            </h3>
            <p className="text-xl text-gray-400">Verifierade recensioner från Google & TikTok</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                text: 'Riktig räddare i nöden! Tappade nyckeln till bilen sent på kvällen. De kom inom 30 minuter och hade löst problemet på mindre än en timme. Proffsigt och vänligt bemötande. Rekommenderar starkt!',
                name: 'Google-kund',
                platform: 'google',
                verified: true,
                rating: 5
              },
              {
                text: 'Toppen service, kunnig personal allt perfekt.',
                name: 'Google-kund',
                platform: 'google',
                verified: true,
                rating: 5
              },
              {
                text: 'Super nöjd med nya nyckeln 🤩🤩🤩 fantastisk bra Service! Rekomenderar starkt för er som behöver en ny bilnyckel.',
                name: 'Robin',
                platform: 'tiktok',
                verified: true,
                rating: 5
              }
            ].map((review, index) => (
              <div key={index} className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-xl p-5 hover:border-blue-500/30 transition-all overflow-hidden">
                <div className="flex items-start justify-between mb-4 gap-2">
                  <div className="flex flex-shrink-0">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {review.verified && (
                      <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30 text-[10px] px-2 py-0.5 whitespace-nowrap">
                        <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                        Verifierad
                      </Badge>
                    )}
                    {review.platform === 'google' && (
                      <Badge className="bg-white/10 text-white border-white/20 text-[10px] px-2 py-0.5 font-semibold whitespace-nowrap">
                        <svg className="h-2.5 w-2.5 mr-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        Google
                      </Badge>
                    )}
                    {review.platform === 'tiktok' && (
                      <Badge className="bg-black border-white/30 text-white text-[10px] px-2 py-0.5 font-semibold whitespace-nowrap">
                        <svg className="h-2.5 w-2.5 mr-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                        </svg>
                        TikTok
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-gray-300 text-sm mb-4 leading-relaxed break-words">{review.text}</p>
                <div className="flex items-center space-x-2 pt-3 border-t border-white/5">
                  <p className="text-white font-semibold text-sm truncate">{review.name}</p>
                  <span className="text-gray-500 flex-shrink-0">•</span>
                  <p className="text-xs text-gray-500 capitalize flex-shrink-0">{review.platform}-recension</p>
                </div>
              </div>
            ))}
          </div>

          {/* TikTok CTA Section */}
          <div className="mb-12 relative">
            <div className="bg-gradient-to-br from-black/40 via-black/60 to-black/40 border-2 border-white/20 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              {/* Animated background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-blue-500/10 animate-pulse"></div>
              
              <div className="relative z-10">
                {/* TikTok Icon */}
                <div className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 bg-black rounded-2xl mb-6 shadow-2xl border-2 border-white/30">
                  <svg className="w-12 h-12 md:w-16 md:h-16" viewBox="0 0 24 24" fill="white">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>

                <h4 className="text-3xl md:text-4xl font-black text-white mb-4">
                  Se Vårt Arbete på <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400">TikTok</span>
                </h4>
                
                <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Följ med bakom kulisserna! Se hur vi löser bilnyckelkriser, programmerar nycklar och hjälper kunder varje dag. 🔑✨
                </p>

                <a
                  href="https://www.tiktok.com/@bilnyckelakutenab"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCTAClick('tiktok_link', 'reviews_section')}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 hover:from-pink-600 hover:via-purple-600 hover:to-blue-600 text-white text-lg md:text-xl font-bold rounded-full transition-all duration-300 shadow-2xl hover:shadow-purple-500/50 hover:scale-105"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  Besök @bilnyckelakutenab
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>

                <div className="mt-6 flex items-center justify-center gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">Riktiga jobben</span>
                  </div>
                  <span>•</span>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">Bakom kulisserna</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Partner - Gävles Autodepå */}
      <section className="py-20 px-4 bg-[#151515]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-black text-white mb-4">
              Vår <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Partner</span>
            </h3>
            <p className="text-xl text-gray-400">Tillsammans erbjuder vi komplett service för ditt fordon</p>
          </div>

          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-purple-500/30 rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {/* Logo */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-purple-600/20 blur-3xl rounded-full"></div>
                  <img 
                    src="/gavles-autodepa-logo.png"
                    alt="Gävles Autodepå"
                    className="relative h-48 md:h-64 w-auto object-contain"
                  />
                </div>
              </div>

              {/* Content */}
              <div>
                <h4 className="text-3xl font-black text-white mb-6">
                  Gävles Autodepå
                  <span className="block text-lg font-normal text-purple-400 mt-2">Bilfix på hjul</span>
                </h4>
                
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    Vi samarbetar med <span className="text-white font-semibold">Gävles Autodepå</span> – en professionell bilverkstad med bred kompetens inom service, reparationer, däckbyte och rullande verkstad.
                  </p>
                  
                  <p>
                    Genom samarbetet kan våra kunder få tillgång till snabb, pålitlig och smidig hjälp även vid andra typer av fordonsrelaterade problem.
                  </p>
                  
                  <p>
                    Gävles Autodepå är kända för sin personliga service, höga kvalitet och sitt arbete med både privatpersoner och företag.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Badge className="bg-purple-600/20 text-purple-400 border-purple-500/30 px-4 py-2">
                    <Wrench className="h-4 w-4 mr-2" />
                    Service & Reparation
                  </Badge>
                  <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30 px-4 py-2">
                    <Car className="h-4 w-4 mr-2" />
                    Rullande Verkstad
                  </Badge>
                  <Badge className="bg-red-600/20 text-red-400 border-red-500/30 px-4 py-2">
                    <Shield className="h-4 w-4 mr-2" />
                    Personlig Service
                  </Badge>
                </div>

                <div className="mt-8">
                  <a 
                    href="https://gavlesautodepa.se/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-full transition-all duration-300 shadow-lg hover:shadow-purple-500/50"
                  >
                    Besök Gävles Autodepå
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">
              <Shield className="inline-block h-4 w-4 text-blue-400 mr-2" />
              Tillsammans erbjuder vi komplett service för alla era fordonsbehov
            </p>
          </div>
        </div>
      </section>

      {/* Partnership & Trust Section */}
      <section className="py-20 px-4 bg-[#151515]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-black text-white mb-4">
              Förtroende Genom <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-red-500">Samarbete</span>
            </h3>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Vi samarbetar med bilverkstäder och bilåterförsäljare som förlitar sig på oss för snabb och professionell hantering av bilnycklar
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-white/10 rounded-2xl p-8 text-center">
              <div className="inline-block p-4 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 mb-4">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3">Bilverkstäder</h4>
              <p className="text-gray-400 leading-relaxed">
                Lokala verkstäder litar på vår expertis när deras kunder behöver nya nycklar eller programmeringshjälp
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-white/10 rounded-2xl p-8 text-center">
              <div className="inline-block p-4 rounded-full bg-gradient-to-br from-red-600 to-red-700 mb-4">
                <Car className="h-10 w-10 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3">Bilåterförsäljare</h4>
              <p className="text-gray-400 leading-relaxed">
                Bilhandlare rekommenderar oss för reservnycklar och akuta situationer med total tillit
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-white/10 rounded-2xl p-8 text-center">
              <div className="inline-block p-4 rounded-full bg-gradient-to-br from-blue-600 to-red-600 mb-4">
                <Award className="h-10 w-10 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3">Etablerat Förtroende</h4>
              <p className="text-gray-400 leading-relaxed">
                Våra partners väljer oss för vår kompetens, pålitlighet och snabba service – gång på gång
              </p>
            </div>
          </div>

          {/* Service Gallery - Car Key Images */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-white text-center mb-8">Vår Expertis inom Bilnycklar</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="relative h-56 rounded-xl overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1652509573480-a190f1c61f6d"
                  alt="Modern car keys service"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <p className="absolute bottom-4 left-4 text-white font-semibold">Moderna bilnycklar</p>
              </div>
              <div className="relative h-56 rounded-xl overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1710006548781-eff5670376fa"
                  alt="Car key fob programming"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <p className="absolute bottom-4 left-4 text-white font-semibold">Key fob programmering</p>
              </div>
              <div className="relative h-56 rounded-xl overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1533558701576-23c65e0272fb"
                  alt="Premium Mercedes key fob"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <p className="absolute bottom-4 left-4 text-white font-semibold">Premium märken</p>
              </div>
              <div className="relative h-56 rounded-xl overflow-hidden group">
                <img 
                  src="https://images.pexels.com/photos/4930676/pexels-photo-4930676.jpeg"
                  alt="Car door unlocking service"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <p className="absolute bottom-4 left-4 text-white font-semibold">Upplåsning</p>
              </div>
            </div>
          </div>

          {/* Professional Equipment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="relative h-80 rounded-xl overflow-hidden group">
              <img 
                src="https://images.unsplash.com/photo-1749477417968-2bc986bc6a42"
                alt="Professional locksmith workshop"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <h5 className="text-2xl font-bold text-white mb-2">Professionell Verkstad</h5>
                <p className="text-gray-200">Modern utrustning för nyckelkopiering och programmering</p>
              </div>
            </div>
            <div className="relative h-80 rounded-xl overflow-hidden group">
              <img 
                src="https://images.pexels.com/photos/4496282/pexels-photo-4496282.jpeg"
                alt="Mobile locksmith service van"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <h5 className="text-2xl font-bold text-white mb-2">Mobil Service</h5>
                <p className="text-gray-200">Vi kommer till dig – var du än befinner dig</p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center bg-gradient-to-r from-blue-600/10 to-red-600/10 border-2 border-blue-500/30 rounded-2xl p-8">
            <p className="text-white text-lg font-semibold mb-2">
              <Shield className="inline-block h-6 w-6 text-blue-400 mr-2" />
              Etablerad sedan 2021
            </p>
            <p className="text-gray-300">
              Med år av erfarenhet och hundratals nöjda kunder, partners och privatpersoner
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-4 bg-[#151515]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-black text-white mb-4">
              Varför Välja <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-blue-500">Oss</span>?
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Snabb Utryckning', description: 'Vi är där du behöver oss – snabbt och effektivt', icon: Zap, color: 'red' },
              { title: 'Dygnet Runt', description: 'Tillgängliga 24/7, 365 dagar om året för akuta ärenden', icon: Clock, color: 'blue' },
              { title: 'Professionell Utrustning', description: 'Modern diagnostik och programmeringsutrustning', icon: Wrench, color: 'red' },
              { title: 'Bred Erfarenhet', description: 'Hanterar många olika bilmärken och modeller', icon: Car, color: 'blue' }
            ].map((item, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:border-red-500/50 transition-all duration-300"
              >
                <div className={`inline-block p-4 rounded-full bg-gradient-to-br ${item.color === 'red' ? 'from-red-600 to-red-700' : 'from-blue-600 to-blue-700'} mb-4`}>
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-3">{item.title}</h4>
                <p className="text-gray-400 leading-relaxed text-lg">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a0a] to-[#111111]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-black text-white mb-4">
              Vanliga Frågor
            </h3>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'Hur snabbt kan ni komma?', a: 'Vi strävar efter att vara på plats inom 30-60 minuter. Vid akuta situationer prioriteras dessa ärenden.' },
              { q: 'Vilka bilmärken arbetar ni med?', a: 'Vi arbetar med de flesta bilmärken och modeller, inklusive BMW, Mercedes, Volvo, Audi, VW, Toyota, och många fler.' },
              { q: 'Vad kostar det?', a: 'Priset varierar beroende på bilmärke och typ av nyckel. Ring 076-005 25 99 för en kostnadsfri prisuppgift.' },
              { q: 'Kan ni hjälpa om jag låst in nycklarna?', a: 'Ja! Vi hjälper dig att låsa upp din bil på ett skonsamt sätt. Ring oss direkt för akut hjälp.' }
            ].map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-white/10 hover:border-red-500/30 rounded-xl px-6 backdrop-blur-sm transition-all"
              >
                <AccordionTrigger className="text-white text-lg font-semibold hover:text-red-400 py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 text-base leading-relaxed pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA after FAQ */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-10">
              <h4 className="text-3xl font-black text-white mb-4">Har du fler frågor?</h4>
              <p className="text-red-100 text-lg mb-6">Ring oss direkt – vi svarar på alla dina frågor!</p>
              <Button 
                onClick={() => {
                  trackCTAClick('faq_cta', 'faq_section');
                  handleCallClick('faq_section');
                }}
                size="lg"
                className="bg-white text-red-600 hover:bg-gray-100 text-xl font-bold px-12 py-8"
              >
                <Phone className="mr-3 h-7 w-7" />
                076-005 25 99
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Replace Contact Form */}
      <section id="contact" className="py-24 px-4 bg-[#151515]">
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="text-5xl md:text-6xl font-black text-white mb-6">
            Tappat <span className="text-red-500">bilnyckeln</span>?<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-blue-500">Ring nu!</span>
          </h3>
          
          <p className="text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Mobil service mellan Stockholm, Hudiksvall och Borlänge – med utgångspunkt i Gävle
          </p>

          <div className="bg-gradient-to-br from-red-600/20 to-blue-600/20 border-4 border-red-500 rounded-3xl p-12 mb-8">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <Phone className="h-12 w-12 text-red-500 animate-pulse" />
              <a 
                href="tel:0760052599"
                onClick={() => trackPhoneClick('final_cta')}
                className="text-6xl md:text-7xl font-black text-white hover:text-red-400 transition-colors"
              >
                076-005 25 99
              </a>
            </div>
            <p className="text-white text-xl font-semibold mb-4">📞 Ring direkt – vi svarar omedelbart</p>
            <p className="text-gray-300 text-lg mb-6">Dygnet runt, 24/7 • Snabb utryckning • Modern utrustning</p>
            
            <div className="border-t border-white/20 pt-6 mt-6">
              <p className="text-white text-lg font-semibold mb-3">Eller skicka e-post:</p>
              <a 
                href="mailto:info@bilnyckelakuten.se"
                className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-xl font-semibold transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>info@bilnyckelakuten.se</span>
              </a>
              <p className="text-gray-400 text-sm mt-2">Vi svarar vanligtvis inom 24 timmar</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <Clock className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <h5 className="text-white font-bold text-lg mb-2">Dygnet Runt</h5>
              <p className="text-gray-400">24/7 Jourservice</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <Zap className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <h5 className="text-white font-bold text-lg mb-2">Snabb Hjälp</h5>
              <p className="text-gray-400">Vi är på plats inom kort</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <Shield className="h-10 w-10 text-blue-500 mx-auto mb-3" />
              <h5 className="text-white font-bold text-lg mb-2">Erfaren Tekniker</h5>
              <p className="text-gray-400">Modern utrustning</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-red-600/20 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="mb-6">
              <img 
                src="/logo.png" 
                alt="Bilnyckel Akuten AB" 
                className="h-24 w-auto object-contain mx-auto mb-6"
              />
            </div>
            
            <div className="bg-gradient-to-r from-red-600/20 to-blue-600/20 border-2 border-red-500/50 rounded-2xl p-8 max-w-3xl mx-auto mb-8">
              <p className="text-white text-xl font-bold mb-3">🚨 Akut hjälp? Ring direkt:</p>
              <a 
                href="tel:0760052599"
                onClick={() => trackPhoneClick('footer')}
                className="text-5xl md:text-6xl font-black text-white hover:text-red-400 transition-colors block"
              >
                076-005 25 99
              </a>
              <p className="text-gray-300 mt-3">Dygnet runt • 24/7 • Snabb utryckning</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 text-center md:text-left">
            <div>
              <h6 className="text-white font-bold mb-3 text-lg">Tjänster</h6>
              <ul className="space-y-2 text-gray-400">
                <li>✓ Ny bilnyckel</li>
                <li>✓ Upplåsning av bil</li>
                <li>✓ Programmering</li>
                <li>✓ Reparation</li>
              </ul>
            </div>
            
            <div>
              <h6 className="text-white font-bold mb-3 text-lg">Serviceområde</h6>
              <ul className="space-y-2 text-gray-400">
                <li>📍 Utgår från Gävle</li>
                <li>🚗 Stockholm</li>
                <li>🚗 Hudiksvall</li>
                <li>🚗 Borlänge</li>
              </ul>
            </div>

            <div>
              <h6 className="text-white font-bold mb-3 text-lg">Kontakt</h6>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center justify-center md:justify-start space-x-2">
                  <Phone className="h-4 w-4 text-red-400" />
                  <a href="tel:0760052599" className="hover:text-white transition-colors">076-005 25 99</a>
                </li>
                <li className="flex items-center justify-center md:justify-start space-x-2">
                  <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:info@bilnyckelakuten.se" className="hover:text-white transition-colors">info@bilnyckelakuten.se</a>
                </li>
                <li>⏰ Dygnet runt, 24/7</li>
              </ul>
              
              {/* Social Media Links */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-white font-semibold mb-3 text-sm">Följ oss</p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  {/* TikTok */}
                  <a 
                    href="https://www.tiktok.com/@bilnyckelakutenab" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                    title="TikTok"
                  >
                    <div className="bg-black border border-white/20 rounded-lg p-2.5 group-hover:border-white/40 transition-colors">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </div>
                  </a>
                  
                  {/* Facebook */}
                  <a 
                    href="https://www.facebook.com/p/Bilnyckel-Akuten-AB-61587683766065/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                    title="Facebook"
                  >
                    <div className="bg-black border border-white/20 rounded-lg p-2.5 group-hover:border-white/40 group-hover:border-blue-500/40 transition-colors">
                      <svg className="w-5 h-5 text-white group-hover:text-blue-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                  </a>
                  
                  {/* Instagram */}
                  <a 
                    href="https://www.instagram.com/bilnyckelakutenab/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                    title="Instagram"
                  >
                    <div className="bg-black border border-white/20 rounded-lg p-2.5 group-hover:border-white/40 group-hover:border-pink-500/40 transition-colors">
                      <svg className="w-5 h-5 text-white group-hover:text-pink-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/5 pt-8 text-center">
            <p className="text-gray-500 text-sm mb-2">
              © 2026 Bilnyckel Akuten Sverige AB. Professionell mobil låssmedsservice.
            </p>
            <p className="text-gray-600 text-xs">
              Alla rättigheter förbehållna.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;